// Contrato del bloque PROMOCIONES de `loan-offerings-business` (fase A — CRUD + bitácora).
//
// 🔴 BLOQUEO DE ORDEN — ESTE MÓDULO NO SE MERGEA ANTES QUE EL OPENAPI DEL LAMBDA.
// `loan-offerings.gen.ts` se generó desde el `openapi/public.yaml` de la rama de promociones del
// lambda, que TODAVÍA NO ESTÁ EN SU `develop`. `source.json` apunta al sibling y `package.json`
// corre `sync-api-types` en el `predev` y el `prebuild`: con el sibling sin los paths
// `/promotions`, el primer `npm run dev` regenera el archivo sin estos schemas y el módulo deja de
// compilar. `build:ci` NO corre el sync, así que CI no lo caza — rompe solo en las máquinas de la
// gente. Mergear primero el lambda, o al menos su openapi.
//
// Los tipos salen del `.gen.ts` y no de `@fiado/type-kit` porque los DTOs de promociones todavía no
// están publicados en la lib; cuando se publiquen, estos alias se reemplazan por los `import type`
// de deep-path sin tocar a los consumidores.
//
//   POST /promotions · GET /promotions · GET /promotions/{id} · PUT /promotions/{id}
//   PUT  /promotions/{id}/status · GET /promotions/log?month=yyyy-mm
//
// FECHAS: ISO-8601 en el wire. MONTOS en cents, PORCENTAJES y TNA en decimal.
import type { UpdateCreditPlanRequest } from '@fiado/type-kit/bin/loanOfferings/dtos/requests/UpdateCreditPlanRequest';
import type { components } from './loan-offerings.gen';

export type PromotionStatusWire = components['schemas']['PromotionStatus'];
export type PromotionPhaseWire = components['schemas']['PromotionPhase'];
export type PromotionEffectKindWire = components['schemas']['PromotionEffectKind'];
export type PromotionLogEventWire = components['schemas']['PromotionLogEvent'];
export type CreditPlanLevelWire = components['schemas']['CreditPlanLevel'];
export type ProductCategoryWire = components['schemas']['ProductCategory'];
export type ProductTierWire = components['schemas']['ProductTier'];

export type PromotionEffectWire = components['schemas']['PromotionEffect'];
export type PromotionEffectInputWire = components['schemas']['PromotionEffectInput'];

/**
 * Lo que devuelve el lambda. TODOS los campos son opcionales en el OpenAPI: el schema de salida
 * no declara `required`, así que el wire puede traer cualquier clave ausente y el mapper es el
 * que decide el valor por defecto de la pantalla (ver `api/mappers.ts`).
 *
 * Los tres campos del segmento entran por intersección: el lambda ya los resuelve —la lista deja
 * de cruzar `GET /segments`— pero el `.gen.ts` commiteado es más viejo y regenerarlo entero
 * traería cambios ajenos a esta pantalla. Van AUSENTES cuando el segmento fue archivado o borrado.
 */
export type PromotionResponse = components['schemas']['PromotionResponse'] & {
    segmentName?: string;
    /** Uno de los 8 slots de la paleta; el mapper descarta cualquier otro valor. */
    segmentColorSlot?: string;
    segmentIsDefault?: boolean;
    /** Centro de costo con que el ledger imputa el bono PCF; ausente = sin asignar. */
    costCenterId?: string;
};

/**
 * La página de `GET /promotions`. El lambda agota sus páginas hasta un tope: con `truncated` en
 * `true` el tenant tiene más promociones de las que vinieron, y la pantalla lo dice.
 */
export interface PromotionListResponse {
    items: PromotionResponse[];
    count: number;
    truncated: boolean;
}
export type PromotionLogEntryResponse = components['schemas']['PromotionLogEntryResponse'];

/**
 * `costCenterId` entra por intersección como los campos del segmento: el `.gen.ts` commiteado es
 * anterior a que el lambda lo aceptara. Al crear, ausente = sin centro; al editar, `null` lo quita.
 */
export type CreatePromotionRequest = components['schemas']['CreatePromotionRequest'] & {
    costCenterId?: string | null;
};
export type UpdatePromotionRequest = components['schemas']['UpdatePromotionRequest'] & {
    costCenterId?: string | null;
};
export type ChangePromotionStatusRequest = components['schemas']['ChangePromotionStatusRequest'];

// ---------------------------------------------------------------------------------------------
// PLANES DE CRÉDITO — el motivo del PUT y la bitácora (`/credit-plans/log` y
// `/credit-plans/{planId}/log`).
//
// Van a mano y no como alias del `.gen.ts`: el generado declara TODOS los campos opcionales, y los
// mappers leen campo por campo — con todo opcional, olvidarse de uno compila igual.
// ---------------------------------------------------------------------------------------------

/**
 * Tope de `reason`, CONTRATO con el backend (su constante `AUDIT_REASON_MAX_LENGTH`): por encima
 * responde 400. La UI corta antes para que el operador no escriba de más y lo descubra al publicar.
 */
export const AUDIT_REASON_MAX_LENGTH = 280;

/**
 * Mínimo del abono a capital del plan. Solo lleva el dato de su modo: `periodPaymentPct` (decimal
 * sobre 1) con PERCENT_OF_PERIOD_PAYMENT y `amountCents` con FIXED. Un plan sin él es ANY.
 */
export type PrepaymentMinimumWire = components['schemas']['PrepaymentMinimum'];
export type PrepaymentMinimumModeWire = PrepaymentMinimumWire['mode'];

/**
 * El body del PUT del plan CON el motivo de auditoría y el mínimo del abono. Los dos ya están en el
 * openapi pero no en el DTO de `@fiado/type-kit` que usa este repo; la intersección los agrega.
 * Cuando se suba la lib, este alias vuelve a ser el DTO pelado.
 */
export type UpdateCreditPlanRequestWire = UpdateCreditPlanRequest & {
    reason?: string;
    prepaymentMinimum?: PrepaymentMinimumWire;
};

/** Qué le pasó al plan: alta, edición de un campo o cambio de estado. */
export type CreditPlanLogActionWire = 'CREATE' | 'UPDATE' | 'STATUS_CHANGE';

/** Un asiento de `SharedAuditTrail` para un plan. */
export interface CreditPlanLogEntryResponse {
    planId: string;
    planName: string | null;
    action: CreditPlanLogActionWire;
    /** Campo del plan que cambió; `null` en el alta, donde cambió la entidad entera. */
    fieldChanged: string | null;
    /** Valores en las unidades del PLAN (fracciones, cents), no en las de la pantalla. */
    oldValue: unknown;
    newValue: unknown;
    occurredAt: string;
    /** VERBATIM del backend: hoy `system` o el `cognitoSub` crudo. */
    performedBy: string;
    /** Nombre legible del actor; `null` mientras nadie lo resuelve — ahí se pinta `performedBy`. */
    performedByName: string | null;
    reason: string | null;
    /** Versión del plan antes del cambio; `null` en el alta, que estrena la v1. */
    versionFrom: number | null;
    /** Versión del plan después del cambio. */
    versionTo: number | null;
}

/** Página de bitácora. `nextCursor` en `null` = no hay más asientos. */
export interface CreditPlanLogPageResponse {
    entries: CreditPlanLogEntryResponse[];
    nextCursor: string | null;
}

/** Paginación de la bitácora: `limit` 1-500 (default 100 del backend) y `cursor` de la página previa. */
export interface CreditPlanLogQuery {
    limit?: number;
    cursor?: string;
}

/**
 * El feed del día agrega el filtro de partición. `day` es `YYYY-MM-DD` en hora de Ciudad de México;
 * sin él el backend responde el día en curso.
 */
export interface CreditPlanLogFeedQuery extends CreditPlanLogQuery {
    day?: string;
}

// ---------------------------------------------------------------------------------------------
// POLÍTICAS ESPECIALES (`/store-policies`) — alimentan el tab Especiales de `/credit/policies`.
//
//   POST /store-policies · GET /store-policies · GET /store-policies/{id} · PUT /store-policies/{id}
//   PUT  /store-policies/{id}/status · GET /store-policies/log · GET /store-policies/{id}/log
//
// UNIDADES: porcentajes y TNA en decimal (0.4 = 40 %, 2.6 = TNA 260 %), montos en cents.
//
// DOS GRUPOS DE PALANCAS: `hardening` ENDURECE el plan y `easing` lo AFLOJA. El backend rechaza la
// misma palanca en los dos grupos (400 CONTRADICTORY_STORE_POLICY_LEVER), así que cada palanca de
// la pantalla lleva una dirección y viaja en UN solo grupo.
// ---------------------------------------------------------------------------------------------

export type StorePolicyStatusWire = components['schemas']['StorePolicyStatus'];
export type StorePolicyPhaseWire = components['schemas']['StorePolicyPhase'];
export type StorePolicyLogActionWire = components['schemas']['StorePolicyLogAction'];
export type StorePolicyHardeningWire = components['schemas']['StorePolicyHardening'];
export type StorePolicyEasingWire = components['schemas']['StorePolicyEasing'];

/**
 * Lo que devuelve el lambda. Igual que en promociones, el schema de salida no declara `required`:
 * cualquier clave puede venir ausente y el mapper decide el valor por defecto de la pantalla.
 *
 * `segmentIds` entra por intersección en las tres formas: el openapi del lambda ya lo declara, pero
 * el `.gen.ts` commiteado es más viejo y regenerarlo entero traería cambios ajenos a esta pantalla.
 */
export type StorePolicyResponse = components['schemas']['StorePolicyResponse'] & {
    segmentIds?: string[] | null;
};
export type StorePolicyLogEntryResponse = components['schemas']['StorePolicyLogEntryResponse'];
export type StorePolicyLogPageResponse = components['schemas']['StorePolicyLogPageResponse'];

export type CreateStorePolicyRequest = components['schemas']['CreateStorePolicyRequest'] & {
    segmentIds?: string[];
};
export type ChangeStorePolicyStatusRequest =
    components['schemas']['ChangeStorePolicyStatusRequest'];

/**
 * Los `code` del bloque de políticas especiales que la pantalla traduce. El `code` ES el contrato
 * con el front; el status solo agrupa por familia. Su mensaje vive en `policies/logic.ts`.
 */
export const STORE_POLICY_ERROR_CODES = [
    'STORE_POLICY_SEGMENT_UNKNOWN',
    'STORE_POLICY_ARCHIVE_REASON_REQUIRED',
] as const;

export type StorePolicyErrorCode = (typeof STORE_POLICY_ERROR_CODES)[number];

/**
 * El parche admite `null` para BORRAR un alcance o una fecha y volver a «todas las tiendas» /
 * «permanente». El openapi declara los arreglos como `minItems: 1` sin `nullable`, pero el DTO que
 * valida de verdad usa `IsOptional() + ArrayNotEmpty()`, y `IsOptional` deja pasar el `null`.
 */
export type UpdateStorePolicyRequest = Omit<
    components['schemas']['UpdateStorePolicyRequest'],
    'allowedSkus' | 'storeIds' | 'targetLevels' | 'targetSkus' | 'validFrom' | 'validUntil'
> & {
    allowedSkus?: string[] | null;
    storeIds?: string[] | null;
    targetLevels?: components['schemas']['CreditPlanLevel'][] | null;
    targetSkus?: string[] | null;
    segmentIds?: string[] | null;
    validFrom?: string | null;
    validUntil?: string | null;
};

/** Paginación de la bitácora: `limit` 1-500 (default 100 del backend) y `cursor` de la página previa. */
export interface StorePolicyLogQuery {
    limit?: number;
    cursor?: string;
}

/** El feed del día agrega la partición. `day` es `YYYY-MM-DD` en hora de Ciudad de México. */
export interface StorePolicyLogFeedQuery extends StorePolicyLogQuery {
    day?: string;
}

// ---------------------------------------------------------------------------------------------
// CATÁLOGO DE ALCANCE (`GET /promotions/scope-catalog`) — el lambda junta las tiendas y los
// productos de TODAS las cadenas del tenant en una sola lectura, y el editor de promociones baja
// de un request por cadena a uno solo. Requiere permiso `loan.promotion.read`.
//
// Va a mano y no como alias del `.gen.ts`: el openapi del lambda todavía no declara este path.
// ---------------------------------------------------------------------------------------------

/** Una tienda elegible del alcance, ya resuelta por el lambda. */
export interface ScopeCatalogStore {
    storeId: string;
    name: string;
    /** Código interno con el que el operador nombra la tienda; entra al buscador. */
    code: string;
    retailerId: string;
}

/** Un producto elegible del alcance. `sku` es lo que se guarda en la promoción. */
export interface ScopeCatalogProduct {
    sku: string;
    brand: string;
    model: string;
    retailerId: string;
}

/**
 * Lo que devuelve el lambda. Con `truncated` en `true` cortó el barrido: faltan tiendas o
 * productos, y la pantalla tiene que dejar teclear el id a mano.
 */
export interface ScopeCatalogResponse {
    stores: ScopeCatalogStore[];
    products: ScopeCatalogProduct[];
    truncated: boolean;
}
